/**
 * The game page's connection to the server (#192), modelled on freedom's
 * `GameSocket` (freedom.salou-open.nl), which learned these in real events:
 *
 * - it reconnects by itself, quickly at first, because a blitz clock keeps
 *   running while a player is away;
 * - a server that has gone silent is treated as gone, even when no close
 *   event ever arrives (a venue's uplink dropping does exactly that);
 * - an action that could not be sent is sent again after reconnecting, so a
 *   move made during a blip is not lost;
 * - once closed it stays closed: leaving the page must not leave a socket
 *   reconnecting in the background.
 */

const PING_MS = 20_000;
/** A healthy server answers every ping; this much silence means it is gone. */
const SILENT_MS = 35_000;

export type ServerMessage = { type: string; [key: string]: unknown };

export class GameSocket {
  private ws: WebSocket | null = null;
  private closed = false;
  private retry = 0;
  private lastHeard = Date.now();
  /** The latest action not yet delivered; sent after the next join. */
  private pending: string | null = null;
  private readonly keepalive: ReturnType<typeof setInterval>;

  constructor(
    private readonly gameId: string,
    private readonly onMessage: (msg: ServerMessage) => void,
    private readonly onStatus: (connected: boolean) => void
  ) {
    this.connect();
    this.keepalive = setInterval(() => {
      if (this.closed || this.ws?.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastHeard > SILENT_MS) this.reconnectNow();
      else this.ws.send(JSON.stringify({ type: "ping" }));
    }, PING_MS);
  }

  /** Send a game action; kept and sent after reconnecting if the line is down. */
  send(action: { type: string; [key: string]: unknown }): void {
    const data = JSON.stringify({ ...action, gameId: this.gameId });
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data);
    else this.pending = data;
  }

  /** Ask for the full state again (after a rejected move, for instance). */
  refresh(): void {
    this.send({ type: "join" });
  }

  close(): void {
    this.closed = true;
    clearInterval(this.keepalive);
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = null;
      ws.close();
    }
  }

  private connect(): void {
    if (this.closed) return;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;

    ws.onopen = () => {
      this.retry = 0;
      this.lastHeard = Date.now();
      this.onStatus(true);
      // The session cookie is not presented on a WebSocket upgrade in a way the
      // server can rely on, so a short-lived token is fetched and sent instead.
      void fetch("/api/auth/ws-token")
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((d: { token?: string } | null) => {
          if (ws !== this.ws || ws.readyState !== WebSocket.OPEN) return;
          if (d?.token) ws.send(JSON.stringify({ type: "auth", token: d.token }));
          ws.send(JSON.stringify({ type: "join", gameId: this.gameId }));
          if (this.pending) {
            ws.send(this.pending);
            this.pending = null;
          }
        });
    };

    ws.onmessage = (event) => {
      this.lastHeard = Date.now();
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string) as ServerMessage;
      } catch {
        return;
      }
      if (msg.type !== "pong") this.onMessage(msg);
    };

    // An error is always followed by a close event, where reconnecting happens.
    ws.onerror = () => {};

    ws.onclose = () => {
      if (this.closed || ws !== this.ws) return;
      this.onStatus(false);
      // Quickly at first, then a little slower, never more than ~2 s apart.
      this.retry = Math.min(this.retry + 1, 6);
      setTimeout(() => this.connect(), 300 * this.retry);
    };
  }

  /** Drop a connection that has gone silent and dial again at once. */
  private reconnectNow(): void {
    const ws = this.ws;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* already gone */
      }
    }
    this.onStatus(false);
    this.connect();
  }
}
